import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutHero — top of the /about page.
 *
 * Structure:
 *  · Left column: eyebrow + big serif headline "The quiet space behind Serene." + subtitle
 *  · Right column: portrait-oriented editorial image
 *  · Bottom scroll hint
 *
 * Motion: staggered translateY 30→0 + opacity 0→1, driven by useEnterAnimation.
 */
export default function AboutHero() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const titleRef = useEnterAnimation<HTMLHeadingElement>({ delay: 140 });
  const subtitleRef = useEnterAnimation<HTMLParagraphElement>({ delay: 320 });
  const metaRef = useEnterAnimation<HTMLDivElement>({ delay: 480 });
  const imageRef = useEnterAnimation<HTMLDivElement>({ delay: 200 });
  const hintRef = useEnterAnimation<HTMLDivElement>({ delay: 640 });

  return (
    <section
      id="about-hero"
      className="relative w-full bg-background-50 overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Section eyebrow / meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / Index
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 01 · Who we are
          </span>
        </div>
      </div>

      {/* Decorative rings */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div className="absolute w-[100vh] h-[100vh] rounded-full border border-foreground-950/10"></div>
        <div className="absolute w-[72vh] h-[72vh] rounded-full border border-foreground-950/[0.08]"></div>
      </div>

      <div className="relative z-10 w-full min-h-screen flex items-center px-6 md:px-10 pt-40 pb-24">
        <div className="max-w-[1500px] w-full mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left column — text */}
          <div className="lg:col-span-6 flex flex-col">
            <div ref={eyebrowRef} className="mb-10">
              <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
                <span className="w-8 h-[1px] bg-foreground-950/35"></span>
                About Serene
              </span>
            </div>

            <h1
              ref={titleRef}
              className="mb-8"
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(2.75rem, 6.5vw, 6.5rem)",
                fontWeight: 300,
                letterSpacing: "-0.03em",
                lineHeight: 1.02,
                color: "oklch(var(--foreground-950))",
                fontVariationSettings: "'opsz' 144",
              }}
            >
              The quiet space
              <br />
              <span className="italic text-foreground-950/85">
                behind Serene.
              </span>
            </h1>

            <p
              ref={subtitleRef}
              className="max-w-[520px] mb-10"
              style={{
                fontFamily: "var(--font-body)",
                fontSize: "clamp(1rem, 1.15vw, 1.2rem)",
                lineHeight: 1.7,
                color: "oklch(var(--foreground-950) / 0.78)",
              }}
            >
              We are three therapists and one small studio in the city. We
              believe therapy should feel like a quiet room with the door
              closed &mdash; unhurried, honest, and yours.
            </p>

            <div ref={metaRef} className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <span
                  className="font-heading"
                  style={{
                    fontSize: "clamp(2.5rem, 3.8vw, 3.75rem)",
                    fontWeight: 300,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: "oklch(var(--foreground-950))",
                    fontVariationSettings: "'opsz' 144",
                  }}
                >
                  09
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-950/60 font-medium leading-tight">
                  Years
                  <br />
                  Practising
                </span>
              </div>
              <span className="w-[1px] h-12 bg-foreground-950/20"></span>
              <div className="flex items-center gap-3">
                <span
                  className="font-heading"
                  style={{
                    fontSize: "clamp(2.5rem, 3.8vw, 3.75rem)",
                    fontWeight: 300,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: "oklch(var(--foreground-950))",
                    fontVariationSettings: "'opsz' 144",
                  }}
                >
                  740+
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-950/60 font-medium leading-tight">
                  Sessions
                  <br />
                  Held
                </span>
              </div>
              <span className="w-[1px] h-12 bg-foreground-950/20 hidden sm:block"></span>
              <div className="flex items-center gap-3">
                <span
                  className="font-heading"
                  style={{
                    fontSize: "clamp(2.5rem, 3.8vw, 3.75rem)",
                    fontWeight: 300,
                    letterSpacing: "-0.03em",
                    lineHeight: 1,
                    color: "oklch(var(--foreground-950))",
                    fontVariationSettings: "'opsz' 144",
                  }}
                >
                  1
                </span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-950/60 font-medium leading-tight">
                  Studio
                  <br />
                  In Lisbon
                </span>
              </div>
            </div>
          </div>

          {/* Right column — image */}
          <div ref={imageRef} className="lg:col-span-6">
            <div
              className="relative w-full overflow-hidden rounded-2xl"
              style={{ aspectRatio: "4 / 5" }}
            >
              <img
                src="https://images.pexels.com/photos/6480707/pexels-photo-6480707.jpeg?auto=compress&cs=tinysrgb&w=1600&h=2000&fit=crop"
                alt="A quiet therapy studio with soft afternoon light and a single linen armchair"
                title="The Serene studio — a quiet room with the door closed"
                className="absolute inset-0 w-full h-full object-cover object-center select-none"
                draggable={false}
              />
              {/* Bottom caption strip */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0"
                style={{
                  height: "42%",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 45%, rgba(0,0,0,0) 100%)",
                }}
              />
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                  <span
                    className="uppercase font-medium"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.3em",
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    The Studio · Lisbon
                  </span>
                </div>
                <span
                  className="uppercase font-medium"
                  style={{
                    fontSize: "10px",
                    letterSpacing: "0.3em",
                    color: "rgba(255,255,255,0.6)",
                  }}
                >
                  Est. 2016
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div
        ref={hintRef}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-foreground-950/55"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
          Scroll to continue
        </span>
        <span className="w-[1px] h-10 bg-foreground-950/35"></span>
      </div>
    </section>
  );
}